import { Button } from '@blueprint/ui';
import { useHistory, useParams } from "react-router-dom";

import { BankStatus, BankAccount } from "../../../types/common";
import { superAdminAccountType, superAdminAccountTypeLow } from "../../../types/superadmin";
import { UserRole, UserCurrent } from "../../../types/userCurrent";
import { isUserSuperAdmin } from "../../../utils/role";
import useDeactivateHandler from "../hooks/useDeactivateHandler";
import useReviewApproveHandler from "../hooks/useReviewApproveHandler";
import useReviewRejectHandler from "../hooks/useReviewRejectHandler";


type AllowedFunctionProps = {
    currentRoles: UserRole[];
    currentUser?: UserCurrent | null;
    bankData: BankAccount;
};

const AllowedFunction = ({ currentRoles, currentUser, bankData }: AllowedFunctionProps): JSX.Element => {
    const history = useHistory();
    const { accountId } = useParams<{ accountId: string }>();
    const { handler: deactivateHandler } = useDeactivateHandler(accountId);
    const { handler: reviewApproveHandler } = useReviewApproveHandler(accountId);
    const { handler: reviewRejectHandler } = useReviewRejectHandler(accountId);
    const accountType = superAdminAccountType(currentRoles);
    const accountTypeLow = superAdminAccountTypeLow(currentRoles);

    const editAccountEle = <Button
        variant="light.primary"
        onClick={() =>
            history.push(`/account-setup/bank-account-details/${accountId}/edit`)
        }
    >
        Edit
    </Button>;
    const deactivateAccountEle = <Button
        className="btn-second-ml"
        variant="light.tertiary"
        onClick={() =>
            deactivateHandler(
                `${bankData.bankAccountHolderName}`,
            )
        }
    >
        {bankData.status === BankStatus.INACTIVE ? "Activate" : "Deactivate"}
    </Button>
    const approveEle = <Button
        variant="light.primary"
        onClick={() =>
            reviewApproveHandler(
                `${accountType}`,
                `${accountTypeLow}`,
                bankData.status,
            )
        }
    >
        Approve
    </Button>;
    const rejectEle = <Button
        variant="light.tertiary"
        onClick={() =>
            reviewRejectHandler(
                `${accountType}`,
                `${accountTypeLow}`,
                bankData.status,
            )
        }
    >
        Reject
    </Button>;


    const content = () => {
        const reg = /(?<=\()(.+?)(?=\))/g;
        const submitter = bankData.submitBy?.match(reg);
        // who can see these functions? --(SuperAdmin)
        if (!(isUserSuperAdmin(currentRoles))) {
            return <></>;
        }
        switch (bankData.status) {
            case BankStatus.DRAFT:
                return editAccountEle;
            case BankStatus.PENDING_APPROVAL:
            case BankStatus.PENDING_EDIT_APPROVAL:
                // cannot edit, other SuperAdmin can review it
                if (!!submitter && submitter[0] !== currentUser?.email) {
                    return <>{rejectEle} {approveEle}</>;
                }
                return <></>;
            case BankStatus.ACTIVE:
                return <>{deactivateAccountEle} {editAccountEle}</>;
            case BankStatus.INACTIVE:
                return editAccountEle;
            default:
                return <></>;
        }
    };

    return content();
};


export default AllowedFunction;
